import { useEffect, useState } from "react";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  CircularProgress,
  Button,
} from "@material-ui/core";

import {
  DetectionRow,
  DetectionDataStatus,
  DetectionHistogramState,
  DetectionAbacusState,
} from "../../redux/detections/detection-types";
import { makeStyles } from "@material-ui/core/styles";

import FlexCol from "../common/FlexCol";
import FlexRow from "../common/FlexRow";
import ResizableSplitPanel from "../common/ResizableSplitPanel";
import { HistogramTimeSlider, IntervalUpdate } from "../common/charts/HistogramTimeSlider";
import DetectionTable from "./DetectionTable";
import DetectionAbacus from "./DetectionAbacus";

type Mode = "table" | "abacus";
export type ZoomOverride = "out" | "in" | null; // for forcing the time slider brush interval to expand or contract

/** having this defined within the function prevented the interval brush from working properly */
const VEGA_ACTIONS = {
  export: { png: true, svg: false },
  compiled: false,
  editor: false,
  source: false,
};

const HISTOGRAM_AXIS_OPTIONS = {
  yAxis: {
    title: "Detections",
    width: 50,
  },
  xAxis: {
    title: "",
  },
};

const useStyles = makeStyles(() => ({
  main: {
    backgroundColor: "white",
  },
  /**
   * The gutter reserves space for the scrollbar so the histogram's x axis aligns
   * with abacus when there are too many receivers and the abacus scrolls
   *
   * This isn't supported on Safari currently :-(
   */
  visArea: {
    flexGrow: 1,
    marginTop: 8,
    overflowY: "auto",
    scrollbarGutter: "stable",
  },
  histArea: {
    flexGrow: 1,
    overflowY: "hidden",
    scrollbarGutter: "stable",
  },
}));

function NavDivider() {
  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        borderRight: "1px solid lightgray",
        marginLeft: "16px",
        marginRight: "16px",
      }}
    ></div>
  );
}

export function DetectionVisualizer({
  detectionState,
  detectionRows,
  detectionCount,
  displayMode,
  setDisplayMode,
  histogram,
  abacus,
  abacusEnabled,
  tableLimitReached,
  binHours,
}: {
  detectionState: DetectionDataStatus;
  detectionRows: DetectionRow[];
  detectionCount?: number;
  displayMode: Mode;
  setDisplayMode: (mode: Mode) => void;
  histogram: DetectionHistogramState;
  abacus: DetectionAbacusState;
  abacusEnabled: boolean;
  tableLimitReached: boolean;
  binHours: 24 | 1;
}) {
  const classes = useStyles();
  const [interval, setInterval] = useState([] as IntervalUpdate); // for HistogramTimeSlider to set selected interval for filtering results
  const [zoomOverride, setZoomOverride] = useState(null as ZoomOverride);
  const [searchText, setSearchText] = useState<string>("");
  const [abacusType, setAbacusType] = useState<string>("colour");
  displayMode = abacusEnabled ? displayMode : "table";
  const timeUnit = binHours === 24 ? "binnedutcyearmonthdate" : "binnedutcyearmonthdatehours";
  const histogramReady = histogram.status === "COMPLETE";
  const histogramSelection =
    histogram.status === "COMPLETE" &&
    !(!interval || interval.length === 0 || interval[0] == interval[1]);

  useEffect(() => {
    setInterval([]);
  }, [detectionCount]);

  return (
    <FlexCol fullWidth fullHeight paddingLevel={1} className={classes.main}>
      <ResizableSplitPanel
        direction="vertical"
        firstInit="20%"
        firstMin={100}
        firstContent={
          <>
            <FlexRow marginLevel={1}>
              {abacusEnabled && (
                <>
                  <FlexCol>
                    <Typography variant="caption">Display</Typography>
                    <RadioGroup
                      row
                      value={displayMode}
                      onChange={e => setDisplayMode(e.target.value as Mode)}
                    >
                      <FormControlLabel
                        value="table"
                        control={<Radio size="small" />}
                        label="Table"
                      />
                      <FormControlLabel
                        value="abacus"
                        control={<Radio size="small" />}
                        label="Abacus"
                      />
                    </RadioGroup>
                  </FlexCol>
                  <NavDivider />
                </>
              )}
              {displayMode === "abacus" && (
                <>
                  <FlexCol>
                    <Typography variant="caption">Abacus Type</Typography>
                    <RadioGroup
                      row
                      value={abacusType}
                      onChange={e => setAbacusType(e.target.value)}
                    >
                      <FormControlLabel
                        value="colour"
                        control={<Radio size="small" />}
                        label="Colour"
                      />
                      <FormControlLabel
                        value="bubble"
                        control={<Radio size="small" />}
                        label="Bubble"
                      />
                    </RadioGroup>
                  </FlexCol>
                  <NavDivider />
                </>
              )}
              <FlexCol>
                <Typography variant="caption">Navigation</Typography>
                <FlexRow
                  itemSpacing={1}
                  paddingLevel={0.5}
                  vAlign="center"
                  title={
                    histogramReady
                      ? "You can select an area of the histogram to specify a time frame for the visualization below it"
                      : "Please wait for data to load"
                  }
                >
                  <Button
                    variant="outlined"
                    size="small"
                    style={{ textTransform: "none" }}
                    onClick={() => setZoomOverride("in")}
                    disabled={!histogramReady}
                  >
                    Zoom In
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    style={{ textTransform: "none" }}
                    onClick={() => setZoomOverride("out")}
                    disabled={!histogramSelection}
                  >
                    Zoom Out
                  </Button>
                </FlexRow>
              </FlexCol>
            </FlexRow>
            <FlexCol className={classes.histArea}>
              {histogram.status === "UNLOADED" ? (
                <div></div>
              ) : ["INITIAL_LOADING"].includes(histogram.status) ? (
                <CircularProgress />
              ) : ["LOADING", "COMPLETE"].includes(histogram.status) ? (
                <HistogramTimeSlider
                  timeUnit={timeUnit}
                  loading={histogram.status === "LOADING"}
                  countPerInterval={histogram.countPerInterval}
                  onIntervalUpdate={setInterval}
                  axisOptions={HISTOGRAM_AXIS_OPTIONS}
                  actions={VEGA_ACTIONS}
                  zoomOverride={zoomOverride}
                  resetZoomOverride={() => setZoomOverride(null)}
                />
              ) : (
                <div>An unexpected error occured</div>
              )}
            </FlexCol>
          </>
        }
        secondContent={
          <FlexCol className={classes.visArea}>
            {displayMode === "table" && (
              <DetectionTable
                tableLimitReached={tableLimitReached}
                detectionState={detectionState}
                detectionRows={detectionRows}
                searchText={searchText}
                setSearchText={setSearchText}
                interval={interval}
                detectionCount={detectionCount}
              />
            )}
            {displayMode === "abacus" &&
              (abacus.status === "UNLOADED" ? (
                <div></div>
              ) : ["INITIAL_LOADING"].includes(abacus.status) ? (
                <CircularProgress />
              ) : ["LOADING", "COMPLETE"].includes(abacus.status) ? (
                <DetectionAbacus
                  timeUnit={timeUnit}
                  loading={abacus.status === "LOADING"}
                  interval={interval}
                  byRxData={abacus.byRx}
                  type={abacusType}
                  actions={VEGA_ACTIONS}
                />
              ) : (
                <div>An unexpected error occured</div>
              ))}
          </FlexCol>
        }
      />
    </FlexCol>
  );
}
